import React, { useState } from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Mail, Building, Phone, Users } from 'lucide-react';
import Button from '../common/Button';
import { EmailService } from '../../services/email/emailService';
import { trackConversion } from '../../utils/tracking/adsTracking';

const DemoRequest = () => {
  const { t } = useTranslatedContent('scale365');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    users: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await EmailService.sendContactForm({
        ...formData,
        product: 'Scale365',
        service: 'Demo Request',
        supportTier: ''
      });
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        users: '',
        message: ''
      });
      trackConversion('contactForm');
    } catch (err) {
      console.error('Form submission error:', err);
      setError(t('demo.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
      <h3 className="text-2xl font-semibold text-white mb-6">{t('demo.title')}</h3>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {t('demo.fields.name')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-blue-300" />
              </div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="block w-full pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('demo.placeholders.name')}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {t('demo.fields.email')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-blue-300" />
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="block w-full pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('demo.placeholders.email')}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {t('demo.fields.company')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building className="h-5 w-5 text-blue-300" />
              </div>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="block w-full pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('demo.placeholders.company')}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              {t('demo.fields.phone')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-5 w-5 text-blue-300" />
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="block w-full pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('demo.placeholders.phone')}
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            {t('demo.fields.users')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Users className="h-5 w-5 text-blue-300" />
            </div>
            <input
              type="number"
              name="users"
              value={formData.users}
              onChange={handleChange}
              required
              min="1"
              className="block w-full pl-10 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('demo.placeholders.users')}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            {t('demo.fields.message')}
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            className="block w-full bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:ring-blue-500 focus:border-blue-500"
            placeholder={t('demo.placeholders.message')}
          />
        </div>

        {error && (
          <div className="bg-red-500/10 text-red-200 p-3 rounded-xl">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 text-green-200 p-3 rounded-xl">
            {t('demo.success')}
          </div>
        )}

        <Button
          type="submit"
          variant="secondary"
          className="w-full bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white border-0"
          disabled={loading}
        >
          {loading ? t('demo.sending') : t('demo.submit')}
        </Button>
      </form>
    </div>
  );
};

export default DemoRequest;